// src/components/admin/AdminLeads.jsx
import React, { useState, useEffect, useMemo } from 'react';
import './AdminLeads.css';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New Lead' },
  { value: 'meeting_booked', label: 'Meeting Booked' },
  { value: 'discovery_done', label: 'Discovery Done' },
  { value: 'follow_up_later', label: 'Follow Up Later' },
  { value: 'ready_for_onboarding', label: 'Ready for Onboarding' },
  { value: 'converted', label: 'Converted to Customer' },
];

const STATUS_CLASSES = {
  new: 'status-new',
  meeting_booked: 'status-meeting',
  discovery_done: 'status-discovery',
  follow_up_later: 'status-followup',
  ready_for_onboarding: 'status-ready',
  converted: 'status-converted',
};

const PAYMENT_OPTIONS = [
  { value: 'not_sent', label: 'Not Sent' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
];

const STORE_OPTIONS = [
  { value: 'not_created', label: 'Not Created' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'ready_for_review', label: 'Ready for Review' },
  { value: 'created', label: 'Created' },
];

const EMPTY_FORM = {
  lead_name: '', business_name: '', email: '', phone: '',
  source: '', call_date: '', sales_rep_id: ''
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [repFilter, setRepFilter] = useState('all');

  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [selectedLead, setSelectedLead] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editPayment, setEditPayment] = useState('');
  const [editStore, setEditStore] = useState('');
  const [editRep, setEditRep] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLeads();
    fetchAdmins();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const res = await fetch('https://creativepulse.pk/main-api/get-leads.php');
      const data = await res.json();
      if (data.success) setLeads(data.leads);
    } catch (e) {
      console.error('Error fetching leads:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const res = await fetch('https://creativepulse.pk/main-api/get-admin-users.php');
      const data = await res.json();
      if (data.success) setAdmins(data.users);
    } catch (e) {
      console.error('Error fetching admins:', e);
    }
  };

  const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleAddLead = async (e) => {
    e.preventDefault();
    if (!formData.lead_name.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('https://creativepulse.pk/main-api/create-lead.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setShowAddModal(false);
        setFormData(EMPTY_FORM);
        fetchLeads();
      } else {
        alert(data.message || 'Failed to add lead');
      }
    } catch (e) {
      alert('Connection error');
    } finally {
      setSubmitting(false);
    }
  };

  const openLead = (lead) => {
    setSelectedLead(lead);
    setEditStatus(lead.status);
    setEditPayment(lead.payment_status);
    setEditStore(lead.store_status);
    setEditRep(lead.sales_rep_id || '');
    setEditNotes(lead.notes || '');
  };

  const closeLead = () => setSelectedLead(null);

  const handleSaveLead = async () => {
    setSaving(true);
    try {
      const res = await fetch('https://creativepulse.pk/main-api/update-lead.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedLead.id,
          status: editStatus,
          payment_status: editPayment,
          store_status: editStore,
          sales_rep_id: editRep || null,
          notes: editNotes
        })
      });
      const data = await res.json();
      if (data.success) {
        closeLead();
        fetchLeads();
      } else {
        alert(data.message || 'Failed to save');
      }
    } catch (e) {
      alert('Connection error');
    } finally {
      setSaving(false);
    }
  };

  const filteredLeads = useMemo(() => {
    return leads.filter(l => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch = !term ||
        (l.lead_name || '').toLowerCase().includes(term) ||
        (l.business_name || '').toLowerCase().includes(term) ||
        (l.email || '').toLowerCase().includes(term) ||
        (l.phone || '').toLowerCase().includes(term);
      const matchesStatus = statusFilter === 'all' || l.status === statusFilter;
      const matchesRep = repFilter === 'all' || String(l.sales_rep_id) === repFilter;
      return matchesSearch && matchesStatus && matchesRep;
    });
  }, [leads, searchTerm, statusFilter, repFilter]);

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setRepFilter('all');
  };

  const statusLabel = (value) => STATUS_OPTIONS.find(s => s.value === value)?.label || value;

  return (
    <div className="admin-leads">
      <div className="leads-header">
        <div>
          <h1>Leads</h1>
          <p>Manage discovery calls, follow-ups, and registration-ready clients.</p>
        </div>
        <div className="leads-header-actions">
          {/* TODO: no CSV format defined yet — stubbed rather than faking it */}
          <button className="btn-outline" onClick={() => alert('Import Leads not implemented yet — needs a defined CSV format.')}>Import Leads</button>
          <button className="btn-outline" onClick={() => alert('Export Leads not implemented yet.')}>Export Leads</button>
          <button className="btn-dark" onClick={() => setShowAddModal(true)}>Add Lead Manually</button>
        </div>
      </div>

      <div className="leads-filter-bar">
        <input
          type="text"
          className="leads-search"
          placeholder="Search name, business, email, phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={repFilter} onChange={(e) => setRepFilter(e.target.value)}>
          <option value="all">All Reps</option>
          {admins.map(a => <option key={a.id} value={a.id}>{a.username}</option>)}
        </select>
        <button className="btn-reset" onClick={resetFilters}>Reset</button>
      </div>

      {loading ? (
        <div className="leads-loader">Loading leads...</div>
      ) : filteredLeads.length === 0 ? (
        <div className="leads-empty">No leads found.</div>
      ) : (
        <div className="leads-table-container">
          <table className="leads-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Business</th>
                <th>Contact</th>
                <th>Source</th>
                <th>Call Date</th>
                <th>Lead Status</th>
                <th>Sales Rep</th>
                <th>Payment</th>
                <th>Store</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map(lead => (
                <tr key={lead.id}>
                  <td className="lead-name-cell">{lead.lead_name}</td>
                  <td>{lead.business_name || '—'}</td>
                  <td>
                    <div className="contact-cell">
                      <span>{lead.email || '—'}</span>
                      <span className="contact-phone">{lead.phone || ''}</span>
                    </div>
                  </td>
                  <td>{lead.source || '—'}</td>
                  <td>{lead.call_date ? new Date(lead.call_date).toLocaleDateString() : '—'}</td>
                  <td><span className={`status-pill ${STATUS_CLASSES[lead.status] || ''}`}>{statusLabel(lead.status)}</span></td>
                  <td>{lead.sales_rep_name || '—'}</td>
                  <td><span className={`mini-pill ${lead.payment_status === 'paid' ? 'mini-green' : 'mini-gray'}`}>{PAYMENT_OPTIONS.find(p => p.value === lead.payment_status)?.label}</span></td>
                  <td><span className={`mini-pill ${lead.store_status === 'created' || lead.store_status === 'ready_for_review' ? 'mini-blue' : 'mini-gray'}`}>{STORE_OPTIONS.find(s => s.value === lead.store_status)?.label}</span></td>
                  <td><button className="view-btn" onClick={() => openLead(lead)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ADD LEAD MODAL */}
      {showAddModal && (
        <div className="lead-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="lead-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add Lead Manually</h2>
            <form onSubmit={handleAddLead}>
              <div className="form-group">
                <label>Lead Name *</label>
                <input type="text" name="lead_name" value={formData.lead_name} onChange={handleFormChange} required />
              </div>
              <div className="form-group">
                <label>Business Name</label>
                <input type="text" name="business_name" value={formData.business_name} onChange={handleFormChange} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleFormChange} />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Source</label>
                  <select name="source" value={formData.source} onChange={handleFormChange}>
                    <option value="">Select source</option>
                    <option value="Website Call">Website Call</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Referral">Referral</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Call Date</label>
                  <input type="date" name="call_date" value={formData.call_date} onChange={handleFormChange} />
                </div>
              </div>
              <div className="form-group">
                <label>Assign Sales Rep</label>
                <select name="sales_rep_id" value={formData.sales_rep_id} onChange={handleFormChange}>
                  <option value="">Unassigned</option>
                  {admins.map(a => <option key={a.id} value={a.id}>{a.username}</option>)}
                </select>
              </div>
              <div className="lead-modal-actions">
                <button type="button" className="btn-outline" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn-dark" disabled={submitting}>{submitting ? 'Adding...' : 'Add Lead'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW / EDIT DRAWER */}
      {selectedLead && (
        <div className="lead-drawer-overlay">
          <div className="lead-drawer-backdrop" onClick={closeLead}></div>
          <div className="lead-drawer">
            <div className="lead-drawer-header">
              <h2>{selectedLead.lead_name}</h2>
              <button className="drawer-close-btn" onClick={closeLead}>×</button>
            </div>
            <div className="lead-drawer-body">
              <div className="lead-info-grid">
                <div><span className="info-label">Business</span><span>{selectedLead.business_name || '—'}</span></div>
                <div><span className="info-label">Email</span><span>{selectedLead.email || '—'}</span></div>
                <div><span className="info-label">Phone</span><span>{selectedLead.phone || '—'}</span></div>
                <div><span className="info-label">Source</span><span>{selectedLead.source || '—'}</span></div>
              </div>

              <div className="form-group">
                <label>Lead Status</label>
                <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Sales Rep</label>
                <select value={editRep} onChange={(e) => setEditRep(e.target.value)}>
                  <option value="">Unassigned</option>
                  {admins.map(a => <option key={a.id} value={a.id}>{a.username}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Payment</label>
                  <select value={editPayment} onChange={(e) => setEditPayment(e.target.value)}>
                    {PAYMENT_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Store</label>
                  <select value={editStore} onChange={(e) => setEditStore(e.target.value)}>
                    {STORE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea rows="4" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Call notes, follow-up context..." />
              </div>

              <button className="btn-dark save-lead-btn" onClick={handleSaveLead} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeads;